import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';

// Catálogo SEO de ciudades chilenas (alineado con CitiesPage CHILEAN_CITIES y server.py VALID_CITIES_CL).
// Slug = path-slug, name = display con acentos, region = nombre real.
export const SEO_CITIES = [
  { slug: 'santiago', name: 'Santiago', region: 'Metropolitana' },
  { slug: 'valparaiso', name: 'Valparaíso', region: 'Valparaíso' },
  { slug: 'vina-del-mar', name: 'Viña del Mar', region: 'Valparaíso' },
  { slug: 'concepcion', name: 'Concepción', region: 'Biobío' },
  { slug: 'antofagasta', name: 'Antofagasta', region: 'Antofagasta' },
  { slug: 'temuco', name: 'Temuco', region: 'La Araucanía' },
  { slug: 'puerto-montt', name: 'Puerto Montt', region: 'Los Lagos' },
  { slug: 'la-serena', name: 'La Serena', region: 'Coquimbo' },
  { slug: 'arica', name: 'Arica', region: 'Arica y Parinacota' },
  { slug: 'rancagua', name: 'Rancagua', region: "O'Higgins" },
  { slug: 'talca', name: 'Talca', region: 'Maule' },
  { slug: 'chillan', name: 'Chillán', region: 'Ñuble' },
  { slug: 'iquique', name: 'Iquique', region: 'Tarapacá' },
  { slug: 'coquimbo', name: 'Coquimbo', region: 'Coquimbo' },
  { slug: 'punta-arenas', name: 'Punta Arenas', region: 'Magallanes' },
];

export const SEO_REGIONES = [
  { slug: 'metropolitana', name: 'Metropolitana' },
  { slug: 'valparaiso', name: 'Valparaíso' },
  { slug: 'biobio', name: 'Biobío' },
  { slug: 'araucania', name: 'La Araucanía' },
  { slug: 'los-lagos', name: 'Los Lagos' },
  { slug: 'los-rios', name: 'Los Ríos' },
  { slug: 'maule', name: 'Maule' },
  { slug: 'ohiggins', name: "O'Higgins" },
  { slug: 'coquimbo', name: 'Coquimbo' },
  { slug: 'antofagasta', name: 'Antofagasta' },
  { slug: 'atacama', name: 'Atacama' },
  { slug: 'tarapaca', name: 'Tarapacá' },
  { slug: 'arica-y-parinacota', name: 'Arica y Parinacota' },
  { slug: 'nuble', name: 'Ñuble' },
  { slug: 'aysen', name: 'Aysén' },
  { slug: 'magallanes', name: 'Magallanes' },
];

/**
 * Bloque SEO con enlazado interno exact-match a ciudades y regiones.
 *
 * Props:
 *  - variant: "gratis" | "general" (cambia anchor text y prefijo).
 *      "gratis"  → "Centros de rehabilitación gratis en {Ciudad}" → /ciudad/centro-rehabilitacion-{slug}
 *      "general" → "Centros de rehabilitación en {Ciudad}"        → /ciudad/centro-rehabilitacion-{slug}
 *  - title (opcional): override del título de la sección.
 *  - testId (opcional): data-testid para el contenedor.
 */
export default function CentersByLocationLinks({ variant = 'general', title, testId }) {
  const cityPrefix = variant === 'gratis'
    ? 'Centros de rehabilitación gratis en'
    : 'Centros de rehabilitación en';
  const regionPrefix = variant === 'gratis'
    ? 'Centros gratuitos en la región de'
    : 'Centros en la región de';

  const heading = title || (variant === 'gratis'
    ? 'Centros gratuitos por ciudad y región'
    : 'Centros de rehabilitación por ciudad y región');

  return (
    <section className="py-14 md:py-20 border-t border-slate-800/60" data-testid={testId || 'centers-by-location'}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-10 max-w-3xl">
          <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-3">
            Directorio nacional
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3">{heading}</h2>
          <p className="text-slate-400">
            Encuentra centros verificados cerca de ti. Selecciona tu ciudad o región para ver el listado actualizado y cotizar gratis.
          </p>
        </div>

        {/* Ciudades — anchor text exact match */}
        <div className="mb-12">
          <h3 className="text-sm font-semibold text-emerald-300 uppercase tracking-wider mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Centros por ciudad
          </h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5">
            {SEO_CITIES.map((city) => (
              <li key={city.slug}>
                <Link
                  to={`/ciudad/centro-rehabilitacion-${city.slug}`}
                  className="group inline-flex items-baseline text-sm text-slate-300 hover:text-emerald-300 transition-colors"
                  data-testid={`seo-city-link-${city.slug}`}
                >
                  <span className="border-b border-transparent group-hover:border-emerald-400/60">
                    {cityPrefix} {city.name}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Regiones — anchor text exact match */}
        <div>
          <h3 className="text-sm font-semibold text-emerald-300 uppercase tracking-wider mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Centros por región
          </h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5">
            {SEO_REGIONES.map((region) => (
              <li key={region.slug}>
                <Link
                  to={`/region/${region.slug}`}
                  className="group inline-flex items-baseline text-sm text-slate-300 hover:text-emerald-300 transition-colors"
                  data-testid={`seo-region-link-${region.slug}`}
                >
                  <span className="border-b border-transparent group-hover:border-emerald-400/60">
                    {regionPrefix} {region.name}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
